import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../../api';

export default function TestQuestionsBySubjectScreen({ token, setError }) {
  const { testId } = useParams();
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/admin/questions?test_id=${testId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setQuestions(res.data.questions || []);
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to load test questions');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token, testId, setError]);

  const groupedBySubject = questions.reduce((acc, q) => {
    const subjectName = q.subject_name || 'Unknown Subject';
    if (!acc[subjectName]) acc[subjectName] = [];
    acc[subjectName].push(q);
    return acc;
  }, {});

  const [expandedSubjects, setExpandedSubjects] = useState({});

  const toggleSubject = (subjectName) => {
    setExpandedSubjects((prev) => ({
      ...prev,
      [subjectName]: !prev[subjectName],
    }));
  };

  return (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Questions by Subject</h2>
          <p className="text-sm text-slate-600">Test ID: {testId}</p>
          <p className="text-xs text-gray-500">Total subjects: {Object.keys(groupedBySubject).length}</p>
        </div>
        <button
          className="bg-gray-200 text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-300"
          onClick={() => navigate(-1)}
        >
          Back
        </button>
      </div>

      {loading ? (
        <div className="text-sm text-gray-600">Loading questions...</div>
      ) : questions.length === 0 ? (
        <div className="text-sm text-gray-600">No questions found for this test.</div>
      ) : (
        Object.keys(groupedBySubject).map((subjectName) => {
          const subjects = groupedBySubject[subjectName];
          const expanded = expandedSubjects[subjectName] ?? true;
          return (
            <div key={subjectName} className="mb-5 border border-gray-200 rounded-lg shadow-sm overflow-hidden">
              <button
                type="button"
                className="w-full flex items-center justify-between px-4 py-3 bg-indigo-50 hover:bg-indigo-100"
                onClick={() => toggleSubject(subjectName)}
              >
                <div className="flex items-center gap-3">
                  <h3 className="text-lg font-semibold text-indigo-700 m-0">{subjectName}</h3>
                  <span className="text-xs font-semibold bg-emerald-100 text-emerald-800 px-2 py-1 rounded-full">
                    {subjects.length} question{subjects.length !== 1 ? 's' : ''}
                  </span>
                </div>
                <span className="text-sm text-indigo-600">{expanded ? 'Collapse ▲' : 'Expand ▼'}</span>
              </button>

              {expanded && (
                <div className="p-4 bg-white space-y-3">
                  {subjects.map((q) => (
                    <div key={q.id} className="p-3 border border-gray-100 rounded-lg hover:border-indigo-200 transition">
                      <div className="flex items-start justify-between gap-4">
                        <div className="space-y-1">
                          <p className="font-medium text-slate-800">{q.text}</p>
                          <p className="text-xs text-slate-500">Difficulty: {q.difficulty || 'N/A'}</p>
                        </div>
                        <button
                          className="text-sm text-indigo-600 hover:text-indigo-800"
                          onClick={() => navigate(`/questions/edit/${q.id}`)}
                        >
                          Edit
                        </button>
                      </div>
                      {q.options && q.options.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {q.options.map((opt) => (
                            <span
                              key={opt.id}
                              className={`px-2 py-1 text-xs rounded-full ${opt.is_correct ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-700'}`}
                            >
                              {opt.text}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}
